import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, User, Trophy, XCircle, CalendarPlus, ExternalLink, Pencil, FileText } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Badge } from "src/components/ui/badge"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/tooltip"

import { useDeal, useWinDeal, useLoseDeal } from "src/features/deals/hooks/useDeals"
import { formatMoney, formatDate, DEAL_STATUS_BADGE, DEAL_STATUS_LABEL } from "src/features/deals/constants"
import { CreateBookingDialog } from "src/features/bookings/components/CreateBookingDialog"
import { EditDealDialog } from "src/features/deals/components/EditDealDialog"
import { QuoteComposerDialog } from "src/features/deals/components/QuoteComposerDialog"
import { NotesPanel } from "src/features/notes/NotesPanel"
import { TasksPanel } from "src/features/tasks/components/TasksPanel"
import { FollowUpsPanel } from "src/features/followups/components/FollowUpsPanel"
import { TagEntitySection } from "src/features/tags/components/TagEntitySection"
import { DocumentList } from "src/features/documents/components/DocumentList"
import { Can } from "src/components/shared/Can"

const DEAL_DOCUMENT_CATEGORIES = ["kyc", "sales_allotment", "agreement", "receipt", "quotation"]

export default function DealDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { deal, loading, reload } = useDeal(id)
  const winDeal = useWinDeal()
  const loseDeal = useLoseDeal()
  const [bookingOpen, setBookingOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [quoteOpen, setQuoteOpen] = useState(false)
  const [docRefresh, setDocRefresh] = useState(0)

  async function handleWin() {
    try {
      await winDeal.mutate(id)
      toast({ title: "Deal won 🎉", description: "You can now create a booking." })
      reload()
    } catch (e) {
      toast({ variant: "destructive", title: "Could not win deal", description: Array.isArray(e) ? e[0] : "" })
    }
  }

  async function handleLose() {
    const reason = window.prompt("Reason for losing this deal? (optional)") ?? undefined
    try {
      await loseDeal.mutate(id, reason)
      toast({ title: "Deal marked lost" })
      reload()
    } catch (e) {
      toast({ variant: "destructive", title: "Could not update deal", description: Array.isArray(e) ? e[0] : "" })
    }
  }

  if (loading && !deal) {
    return <><div className="px-8 py-7"><Skeleton className="h-40 w-full max-w-2xl rounded-2xl" /></div></>
  }
  if (!deal) {
    return <><div className="px-8 py-7 text-sm text-slate-400">Deal not found.</div></>
  }

  const isWon = deal.status === "won"
  const isOpen = deal.status === "open"

  return (
    <>
      <div className="px-8 py-7 space-y-5">
        <Button variant="ghost" onClick={() => navigate("/deals")} className="inline-flex h-auto items-center gap-1.5 p-0 text-sm font-normal text-slate-500 hover:bg-transparent hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back to deals
        </Button>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">{deal.name || `Deal #${deal.id}`}</h1>
              <Badge variant={DEAL_STATUS_BADGE[deal.status] || "secondary"}>
                {DEAL_STATUS_LABEL[deal.status] || deal.status}
              </Badge>
            </div>
            <p className="mt-1 text-[13px] text-slate-500">
              {deal.stage?.name || "—"} · {formatMoney(deal.value)}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Can resource="deals" action="update">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            </Can>
            <Can resource="payments" action="generate_document">
              {deal.unit_id || deal.project_id ? (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setQuoteOpen(true)}>
                  <FileText className="h-4 w-4" /> Quotation
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button variant="outline" size="sm" className="gap-1.5" disabled>
                          <FileText className="h-4 w-4" /> Quotation
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Link a project or unit to this deal to generate a quote.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </Can>
            {isOpen && (
              <Can resource="deals" action="update">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={handleLose} disabled={loseDeal.isPending}>
                  <XCircle className="h-4 w-4 text-red-500" /> Mark lost
                </Button>
                <Button size="sm" className="gap-1.5" onClick={handleWin} disabled={winDeal.isPending}>
                  <Trophy className="h-4 w-4" /> Mark won
                </Button>
              </Can>
            )}
            {isWon && (
              <Can resource="bookings" action="create">
                <Button size="sm" className="gap-1.5" onClick={() => setBookingOpen(true)}>
                  <CalendarPlus className="h-4 w-4" /> Create booking
                </Button>
              </Can>
            )}
          </div>
        </div>

        {/* Tags */}
        <TagEntitySection
          taggableType="Deal"
          taggableId={deal.id}
          initialTags={deal.tags || []}
          variant="inline"
          onChanged={reload}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
          {/* Lead card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-xs text-slate-400">Lead</div>
            {deal.lead ? (
              <Button
                variant="ghost"
                onClick={() => navigate(`/leads/${deal.lead.id}`)}
                className="flex h-auto w-full items-center justify-start gap-3 rounded-lg p-1.5 text-left font-normal hover:bg-slate-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
                  {(deal.lead.name || "?").slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-800">{deal.lead.name}</div>
                  <div className="truncate text-xs text-slate-400">{deal.lead.phone || deal.lead.status}</div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-slate-300" />
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400"><User className="h-4 w-4" /> No lead linked</div>
            )}
          </div>

          {/* Details + notes */}
          <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-3 text-xs text-slate-400">Deal details</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <Field k="Value" v={formatMoney(deal.value)} />
              <Field k="Stage" v={deal.stage?.name} />
              <Field k="Owner" v={deal.owner?.full_name} />
              {deal.channel_partner && (
                <Field k="Channel Partner" v={`${deal.channel_partner.name} (${deal.channel_partner.referral_code || "no code"})`} />
              )}
              <Field k="Expected close" v={formatDate(deal.expected_close_on)} />
              {deal.won_at && <Field k="Won on" v={formatDate(deal.won_at)} />}
              {deal.lost_at && <Field k="Lost on" v={formatDate(deal.lost_at)} />}
              {deal.lost_reason && <Field k="Lost reason" v={deal.lost_reason} />}
              <Field k="Created" v={formatDate(deal.created_at)} />
            </dl>
          </div>

          <FollowUpsPanel parentType="deals" parentId={id} />
          <TasksPanel parentType="deals" parentId={id} />
          <DocumentList scope={{ type: "deal", id }} categories={DEAL_DOCUMENT_CATEGORIES} refreshKey={docRefresh} />
          <NotesPanel notable="deals" id={id} />
          </div>
        </div>
      </div>

      <CreateBookingDialog
        open={bookingOpen}
        deal={deal}
        onClose={() => setBookingOpen(false)}
        onSuccess={(booking) => booking?.id && navigate(`/bookings/${booking.id}`)}
      />

      <EditDealDialog
        open={editOpen}
        deal={deal}
        onClose={() => setEditOpen(false)}
        onSaved={reload}
      />

      <QuoteComposerDialog
        open={quoteOpen}
        deal={deal}
        onClose={() => setQuoteOpen(false)}
        onGenerated={() => setDocRefresh((n) => n + 1)}
      />
    </>
  )
}

function Field({ k, v }) {
  return (
    <div>
      <dt className="text-[11px] text-slate-400">{k}</dt>
      <dd className="text-slate-700">{v || "—"}</dd>
    </div>
  )
}
